<template>
  <svg
    :class="['clara-strands', `clara-strands--${tone}`]"
    viewBox="0 0 400 120"
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <!-- Ids únicos por instância: modal e painel podem coexistir, e dois
           <defs> com o mesmo id fazem o segundo herdar o gradiente do primeiro. -->
      <linearGradient
        :id="gradientId"
        x1="0"
        y1="0"
        x2="1"
        y2="0"
      >
        <stop
          offset="0"
          class="clara-strands__borda"
        />
        <stop
          :offset="fade"
          class="clara-strands__meio"
        />
        <stop
          offset="0.5"
          class="clara-strands__topo"
        />
        <stop
          :offset="1 - fade"
          class="clara-strands__meio"
        />
        <stop
          offset="1"
          class="clara-strands__borda"
        />
      </linearGradient>

      <filter
        :id="blurId"
        x="-20%"
        y="-60%"
        width="140%"
        height="220%"
      >
        <feGaussianBlur :stdDeviation="blur" />
      </filter>
    </defs>

    <g :filter="`url(#${blurId})`">
      <path
        v-for="fio in strands"
        :key="fio.classe"
        :class="['clara-strands__fio', fio.classe]"
        :d="fio.d"
        :stroke="`url(#${gradientId})`"
      />
    </g>
  </svg>
</template>

<script>
/**
 * Fios luminosos de fundo da Clara.
 *
 * Duas leituras, uma por superfície de destino — o mesmo desenho não serve para
 * as duas porque o modo de composição é oposto:
 *   · `tone="dark"`  brilho ADITIVO (`screen`), para o herói escuro do modal;
 *   · `tone="light"` os fios PINTAM (`multiply`), para o corpo claro do painel,
 *                    onde o aditivo não desenharia absolutamente nada.
 * No tema escuro o `light` volta ao aditivo: lá a superfície é escura de novo.
 */

/* A animação desloca exatamente PERIOD, o mesmo em que o caminho se repete.
   É o que faz o loop não ter emenda; mudar um sem o outro cria um salto. */
const PERIOD = 200;

const wave = (amplitude) => {
  let d = `M-${PERIOD},60`;

  for (let x = -PERIOD; x < 800; x += 100) {
    const direction = (x / 100) % 2 === 0 ? -amplitude : amplitude;

    d += ` q 50,${direction} 100,0`;
  }

  return d;
};

export default {
  name: 'ClaraStrands',

  props: {
    blur: {
      type: Number,
      default: 3.2
    },

    /* A superfície ONDE os fios vão, não a cor deles. */
    tone: {
      type: String,
      default: 'dark',
      validator: (v) => ['dark', 'light'].includes(v)
    }
  },

  data() {
    return {
      strands: [
        { classe: 'clara-strands__fio--1', d: wave(26) },
        { classe: 'clara-strands__fio--2', d: wave(17) },
        { classe: 'clara-strands__fio--3', d: wave(34) }
      ]
    };
  },

  computed: {
    /* Quanto de cada lado o fio leva para acender.
       No herói ESCURO o esmaecido é longo: ali os fios flutuam dentro de uma
       caixa e a ponta acesa encostando na aresta lê como corte.
       Na faixa CLARA o desenho tem que ir de lado a lado — com 0,28 as ondas
       morriam a um terço da largura e a faixa virava uma mancha no meio. */
    fade() {
      return this.tone === 'light' ? 0.1 : 0.28;
    },

    gradientId() {
      return `clara-strands-g-${this._uid}`;
    },

    blurId() {
      return `clara-strands-b-${this._uid}`;
    }
  }
};
</script>

<style scoped>
.clara-strands {
  display: block;
  width: 100%;
  height: 100%;
}

/* O pico é o ponto de maior CONTRASTE contra a superfície, e por isso troca de
   tom junto com ela: sobre o escuro o claro sobe, sobre o claro o escuro desce.
   Trocar só o blend, mantendo o pico claro, deixaria a onda quase invisível no
   branco — #98badc rende 2,02 ali. */
.clara-strands--dark {
  --clara-fio: var(--clara-brand);
  --clara-fio-pico: var(--clara-brand-soft);
}

.clara-strands--light {
  --clara-fio: var(--clara-brand-soft);
  --clara-fio-pico: var(--clara-brand);
}

.clara-strands__borda {
  stop-color: var(--clara-fio);
  stop-opacity: 0;
}

.clara-strands__meio {
  stop-color: var(--clara-fio);
}

.clara-strands__topo {
  stop-color: var(--clara-fio-pico);
}

.clara-strands__fio {
  fill: none;
  stroke-linecap: round;
}

.clara-strands--dark .clara-strands__fio {
  mix-blend-mode: screen;
}

/* Fundo é fundo: a opacidade na raiz (e não no fio) porque a animação de pulso
   já anima `opacity` — as duas no mesmo elemento e a animação venceria. */
.clara-strands--light {
  opacity: 0.7;
}

.clara-strands--light .clara-strands__fio {
  mix-blend-mode: multiply;
}

/* Tema escuro: o corpo do painel volta a ser superfície escura, e com ele
   voltam o brilho aditivo e o pico claro. */
.v-application.theme--dark .clara-strands--light {
  --clara-fio: var(--clara-brand);
  --clara-fio-pico: var(--clara-brand-soft);
  opacity: 1;
}

.v-application.theme--dark .clara-strands--light .clara-strands__fio {
  mix-blend-mode: screen;
}

/* Durações primas entre si: os strands nunca reencontram o mesmo alinhamento. */
.clara-strands__fio--1 {
  stroke-width: 3.5;
  animation: clara-strands-desliza 9s linear infinite, clara-strands-pulsa 6s ease-in-out infinite;
}

.clara-strands__fio--2 {
  stroke-width: 2.2;
  animation: clara-strands-desliza 13s linear infinite reverse,
    clara-strands-pulsa 7.5s ease-in-out infinite -2s;
}

.clara-strands__fio--3 {
  stroke-width: 2.8;
  animation: clara-strands-desliza 17s linear infinite,
    clara-strands-pulsa 9s ease-in-out infinite -4s;
}

@keyframes clara-strands-desliza {
  to {
    transform: translateX(200px);
  }
}

@keyframes clara-strands-pulsa {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .clara-strands__fio {
    animation: none;
  }
}
</style>
